import type { IOgGridTableProps } from '@opengov/components-og-grid-table';
import { ColumnTypes } from '../../../../src/components/table/columnTypes';
import { IServerSideDatasource } from 'ag-grid-community';
import {
  EOgqlJobState,
  IOgqlJobResult,
  IOgqlQueryResult,
  IOgqlV1JobResult,
  IOgqlV1QueryResult
} from '@opengov/ogql-types';
import { IGroupByAgGridRowItem, ogqlDataToAgGridGroupBy } from '@opengov/reporting-transforms/summaryTable';
import { removeNoCodes } from '@opengov/reporting-transforms/visualizations';
import {
  createAgGridColumnConfig,
  EQueryType,
  FilterOperators,
  IColumnFilter,
  IReportConfiguration,
  OG_CHILD_COUNT_COLUMN_NAME
} from '@opengov/reporting-types';
import {
  EReportColumnTypes,
  getColumnByNameFromList,
  getColumnName,
  getDisplayName
} from '@opengov/reporting-types/src/reportColumns-immer';
import isBlank from '@sedan-utils/is-blank';
import { produce } from 'immer';
import { createSelector } from 'reselect';
import {
  reportActionCreators,
  reportConfigurationActionCreators,
  reportExplorerActionCreators
} from '../../../../module/state';
import { selectBypassColumnMultiFilterLimitFeatureFlag } from '../../../common/flags';
import { getTableColumnTypeFromReportColumnType } from '../../../common/helpers/semanticTypeToColumnType';
import { reportLocaleText } from '../../../common/locale';
import { getCalculationString, getCondBreakdown } from '../../../common/modals/components/utilities';
import { selectCurrentReportId } from '../../../common/report/selectors/reportSelectors';
import { IRunV1Query, runV0Query } from '../../../common/reportConfiguration/effects/queries/runQueries';
import { reportConfigurationEffects } from '../../../common/reportConfiguration/effects/reportConfigurationEffects';
import {
  indicesOfHiddenColumns,
  removeHiddenData
} from '../../../common/reportConfiguration/effects/reportQueryEffectResponders';
import { selectAgGridColumnConfig } from '../../../common/reportConfiguration/reportColumnSelectors';
import {
  selectDetailTableGroupByColumns,
  selectDetailTableShowTotals,
  selectIsInStandardReport
} from '../../../common/reportConfiguration/selectors';
import { selectCurrentLens } from '../../../common/reportTemplate/standardTemplateSelectors';
import { selectCurrentSavedView } from '../../../common/savedViews/selectors';
import {
  selectDataTableStatus,
  selectDetailTableColumns,
  selectReportConfigWithMetadata,
  selectReportTotalRowCount
} from '../../selectors/selectReportExplorerState';
import { DataTableStatus } from '../../types';
import { reportDataTableAgGridSelector } from '../state/reportDataTableAgGridState';

import { gridOptions } from './gridOptions';
import { selectAgGridColumnTypes } from './selectAgGridColumnTypes';
import { selectFilterProps } from './selectFilterProps';
import { MuiToastTypes } from '../../../common/helpers/utils';

const HEADER_LENGTH_MAX = 20;

export const removeNoCodesFromRow = (row: any[]) => {
  return row.map((val) => {
    return removeNoCodes(val, '');
  });
};

export const selectAgGridJobResult = createSelector(reportDataTableAgGridSelector, (state) => state.jobResult);
export const selectAgGridSumJobResult = createSelector(reportDataTableAgGridSelector, (state) => state.sumJobResult);

const getColumnDefs = ({ reportConfiguration, locale, agGridColumnConfig, includeGroupByColumn }) => {
  if (!reportConfiguration) return [];

  const columnRenames = reportConfiguration.presentationConfiguration.columnNames;
  const filters = reportConfiguration.detailTableQuery.filter;
  const sortColumn = reportConfiguration.detailTableQuery.sortColumn;
  const { columns } = reportConfiguration.detailTableQuery;
  // selectReportConfigWithMetadata will always provide the most up to date column metadata
  // for both the lens/view source columns and the joined dimensional columns
  const removableIndices = indicesOfHiddenColumns(reportConfiguration.detailTableQuery.columns);

  let filteredColumns = columns
    .filter((column, ndx) => !removableIndices.includes(ndx))
    // .filter(column => !reportConfiguration.detailTableQuery.groupByColumns.includes(column.name))
    .map((column) => {
      // look up the existing column config based on the column name
      // if that fails, fall back to the simple name
      const agGridConfigForColumn =
        agGridColumnConfig?.find((col) => {
          return col.columnName === column.name;
        }) ||
        agGridColumnConfig?.find((col) => {
          return col.columnName === getColumnName(column.name);
        });

      const columnType = getTableColumnTypeFromReportColumnType(column);
      const showTooltip =
        getDisplayName(column, columnRenames).length > HEADER_LENGTH_MAX ||
        column.columnType === EReportColumnTypes.conditional ||
        column.columnType === EReportColumnTypes.infix;
      const tooltipContent =
        column.columnType === EReportColumnTypes.conditional
          ? `${locale.reportTable.columnEdit.condHeader}: ${getCondBreakdown(column, locale)}`
          : column.columnType === EReportColumnTypes.infix
            ? `${locale.reportTable.columnEdit.calcHeader}: ${getCalculationString(column)}`
            : null;
      return {
        headerName: getDisplayName(column, columnRenames),
        colId: column.name,
        field: column.name,
        type: columnType,
        // Determines whether or not the column header and cells are right aligned
        isValueColumn: columnType !== ColumnTypes.Text,
        showTooltip,
        tooltipBody: tooltipContent,
        headerComponentParams: {
          isFiltered: filters.find((filter) => filter.columnName === column.name),
          isSorted: sortColumn === column.name
        },
        pinned: agGridConfigForColumn?.pinned,
        hide: agGridConfigForColumn?.hide,
        sortedIndex: agGridConfigForColumn?.sortedIndex
      };
    })
    // sort the columns according to the stored sort order
    .sort((a, b) => (a.sortedIndex < b.sortedIndex ? -1 : 0))
    // and then remove the sortedIndex property to avoid an avalanche of console warnings from ag-grid
    .map((a) => {
      if (a) delete a.sortedIndex;
      return a;
    });

  if (includeGroupByColumn) {
    const groupByColumn = filteredColumns.find((column) =>
      reportConfiguration.detailTableQuery.groupByColumns.includes(column.field)
    );
    filteredColumns = filteredColumns.filter(
      (column) => !reportConfiguration.detailTableQuery.groupByColumns.includes(column.field)
    );
    if (groupByColumn) {
      filteredColumns.unshift(groupByColumn);
    }
    return filteredColumns;
  } else {
    return filteredColumns.filter(
      (column) => !reportConfiguration.detailTableQuery.groupByColumns.includes(column.field)
    );
  }
};

export const selectAgGridColumnDefsWithGroupByColumn = createSelector(
  selectReportConfigWithMetadata,
  reportLocaleText,
  selectAgGridColumnConfig,
  (reportConfiguration, locale, agGridColumnConfig) => {
    return getColumnDefs({ reportConfiguration, locale, agGridColumnConfig, includeGroupByColumn: true });
  }
);

export const selectAgGridColumnDefs = createSelector(
  selectReportConfigWithMetadata,
  reportLocaleText,
  selectAgGridColumnConfig,
  (reportConfiguration, locale, agGridColumnConfig) => {
    return getColumnDefs({ reportConfiguration, locale, agGridColumnConfig, includeGroupByColumn: false });
  }
);

export const jobResultToAggridRowData = (
  jobResult: IOgqlQueryResult | IOgqlV1QueryResult
): { [key: string]: number | string }[] => {
  const { data } = jobResult;
  let columns;
  if ('metadata' in jobResult) {
    columns = jobResult.metadata.columns;
  } else {
    columns = jobResult.columns;
  }
  const removableIndices = indicesOfHiddenColumns(columns);
  const rawRowData = removeHiddenData(data, removableIndices);
  const visibleColumns = columns.filter((column, ndx) => !removableIndices.includes(ndx));
  return rawRowData
    .map((row) => {
      // when no data comes back the first row is an empty array so we need to filter it out
      if (!row.length) {
        return null;
      }
      return removeNoCodesFromRow(row).reduce((acc, value, index) => {
        acc[visibleColumns[index]?.name] = value;
        return acc;
      }, {});
    })
    .filter((row) => !!row);
};

export const selectAgGridRowData = createSelector(
  selectReportConfigWithMetadata,
  selectAgGridJobResult,
  (reportConfig, jobResult) => {
    let rowData = null;

    if (jobResult && reportConfig) {
      rowData = jobResultToAggridRowData(jobResult);
    }

    return rowData;
  }
);

export const selectAgGridSumRowData = createSelector(
  selectReportConfigWithMetadata,
  selectAgGridSumJobResult,
  (reportConfig, jobResult) => {
    let rowData = null;

    if (jobResult && reportConfig) {
      rowData = jobResultToAggridRowData(jobResult);
    }

    return rowData;
  }
);

export const buildDetailTableQueryParams = ({
  groupKeys,
  reportConfiguration,
  isStandardTemplate,
  childRequest,
  limit,
  offset,
  emptyValuesText
}: {
  groupKeys: string[];
  reportConfiguration: IReportConfiguration;
  isStandardTemplate: boolean;
  childRequest: boolean;
  limit: number;
  offset: number;
  emptyValuesText: string;
}): IRunV1Query => {
  let reportConfig = reportConfiguration;
  const value = groupKeys[0] === emptyValuesText ? '' : groupKeys[0];
  const operator = groupKeys[0] === emptyValuesText ? FilterOperators.Blank : FilterOperators.Equal;

  const categorizedByColumnName = reportConfiguration.visualizationQuery.categorizedByColumn.name;

  if (childRequest) {
    reportConfig = produce(reportConfiguration, (draftConfig: IReportConfiguration) => {
      // we are adding a filter but not a filter boolean operator on purpose
      // see: queryOfDetailTable.ts parentFilter
      draftConfig.detailTableQuery.filter.push({
        columnName: categorizedByColumnName,
        operator,
        value
      } as IColumnFilter);
      // clear the group by because this is a child request
      // to support multiple levels this logic would need to change
      draftConfig.detailTableQuery.groupByColumns = [];
    });
  }

  return {
    queryType: childRequest ? EQueryType.detailTableGroupChildQuery : EQueryType.detailTableQuery,
    childParentKey: childRequest ? reportConfiguration.detailTableQuery.groupByColumns.toString() : null,
    useLargeScaleQueryLimit: false,
    reportConfiguration: reportConfig,
    useQueryService: true,
    isStandardTemplate,
    limit,
    offset
  };
};

export const formatParentAndCount = (parentLabel: string, count: number, emptyValuesText: string): string =>
  `${isBlank(parentLabel) ? emptyValuesText : parentLabel} (${count})`;

export const handleDetailTableParentJobResult = (
  result: IOgqlV1JobResult | IOgqlJobResult,
  groupByColumnName: string,
  emptyValuesText: string
): IGroupByAgGridRowItem[] => {
  const columns = (result as IOgqlV1JobResult).jobResult.metadata
    ? (result as IOgqlV1JobResult).jobResult.metadata.columns
    : (result as IOgqlJobResult).jobResult.columns;
  const childCountIndex = columns.findIndex((column) => column.name === OG_CHILD_COUNT_COLUMN_NAME);

  const data = result.jobResult.data.reduce((acc, queryRowData) => {
    const newRowData = {};
    const childCount = queryRowData[childCountIndex];

    queryRowData.forEach((cell, index) => {
      if (!isBlank(childCount) && columns[index].name === groupByColumnName) {
        newRowData[columns[index].name] = formatParentAndCount(cell, childCount, emptyValuesText);
      } else {
        newRowData[columns[index].name] = cell;
      }
    });
    acc.push(newRowData);

    return acc;
  }, []);

  return ogqlDataToAgGridGroupBy(data, columns, true, '');
};

export const handleDetailTableChildJobResult = (
  result: IOgqlV1JobResult | IOgqlJobResult,
  groupByColumnName: string
): IGroupByAgGridRowItem[] => {
  const columns = (result as IOgqlV1JobResult).jobResult.metadata
    ? (result as IOgqlV1JobResult).jobResult.metadata.columns
    : (result as IOgqlJobResult).jobResult.columns;
  const data = result.jobResult.data.reduce((acc, queryRowData) => {
    const newRowData = {};
    queryRowData.forEach((cell, index) => {
      newRowData[columns[index].name] = cell;
    });
    acc.push(newRowData);

    return acc;
  }, []);

  return ogqlDataToAgGridGroupBy(data, columns, false, groupByColumnName);
};

export const handleDetailTableServerSideDataSourceResult = (
  result: IOgqlJobResult,
  groupKeys: string[],
  groupByColumnName: string,
  limit: number,
  offset: number,
  success: (params: { rowData: any[]; rowCount?: number | null }) => void,
  emptyValuesText: string
): void => {
  // it can be cancelled ...
  if (result.state === EOgqlJobState.finished) {
    if (result?.jobResult?.data?.length && result.jobResult.data[0].length !== 0) {
      const transformedData = groupKeys?.length
        ? handleDetailTableChildJobResult(result, groupByColumnName)
        : handleDetailTableParentJobResult(result, groupByColumnName, emptyValuesText);
      const last = transformedData.length < limit ? offset + transformedData.length : undefined;

      success({ rowData: transformedData, rowCount: last });
    } else {
      success({ rowData: [], rowCount: 0 });
    }
    reportExplorerActionCreators.updateDataTableStatus(DataTableStatus.Current);
  }
};

export const selectDetailTableServerSideDataSource = createSelector(
  selectReportConfigWithMetadata,
  selectCurrentLens,
  selectIsInStandardReport,
  selectCurrentReportId,
  reportLocaleText,
  (reportConfiguration, standardLens, isStandardTemplate, reportId, reportMsgs): IServerSideDatasource => ({
    getRows(params) {
      const emptyValuesText = reportMsgs.reportExplorer.emptyValues;
      const offset = params.request.startRow;
      const limit = params.request.endRow - offset;
      const groupKeys = params.request.groupKeys;
      const groupByColumnName = reportConfiguration.detailTableQuery.groupByColumns[0];

      // no groupKeys means this is not a request for children
      // see: https://www.ag-grid.com/javascript-grid-server-side-model-grouping/#row-grouping-on-the-server

      const queryParams = buildDetailTableQueryParams({
        groupKeys,
        reportConfiguration,
        isStandardTemplate,
        childRequest: !!groupKeys?.length,
        limit,
        offset,
        emptyValuesText
      });

      runV0Query({
        ...queryParams,
        reportId,
        body: isStandardTemplate
          ? { reportConfiguration: queryParams.reportConfiguration }
          : queryParams.reportConfiguration,
        lensId: isStandardTemplate ? standardLens.id : ''
      })
        .then((result) => {
          handleDetailTableServerSideDataSourceResult(
            result,
            groupKeys,
            groupByColumnName,
            limit,
            offset,
            params.success,
            emptyValuesText
          );

          // only open parents if this was the parent request
          if (groupKeys.length === 0) {
            const groupNodes = [];
            params.api.forEachNode((node) => {
              if (node.data && node.data.isGroup) {
                groupNodes.push(node);
              }
            });

            groupNodes.forEach((node) => {
              const nodeShouldBeExpanded =
                reportConfiguration?.presentationConfiguration?.groupByExpandedNodes?.includes(node.key);
              if (nodeShouldBeExpanded && !node.expanded) {
                node.setExpanded(true);
              }
            });
          }
        })
        .catch((err) => {
          console.error(err);

          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.FAILURE,
            headerText: reportMsgs.query.failure.headerText,
            bodyText: reportMsgs.query.failure.bodyText,
            dataTest: `toast-load-report-failure`
          });
        });
    }
  })
);

export const selectAgGridPropsExtra = createSelector(
  selectDetailTableGroupByColumns,
  selectDetailTableServerSideDataSource,
  selectReportConfigWithMetadata,
  selectCurrentSavedView,
  (groupByColumns, detailTableServerSideDataSource, reportConfig, savedView) => ({
    groupByColumns,
    detailTableServerSideDataSource,
    reportConfig,
    savedView
  })
);
const childCountInParentName = /\s\(\d+\)$/;

export const selectDataTableAgGridProps = createSelector(
  selectDataTableStatus,
  selectDetailTableColumns,
  selectAgGridColumnDefs,
  selectAgGridRowData,
  selectFilterProps,
  selectAgGridColumnTypes,
  selectReportTotalRowCount,
  selectBypassColumnMultiFilterLimitFeatureFlag,
  selectAgGridColumnConfig,
  selectDetailTableShowTotals,
  selectAgGridSumRowData,
  selectAgGridPropsExtra,
  (
    tableStatus,
    tableColumns,
    columnDefs,
    rowData,
    filterProps,
    columnTypes,
    rowCount,
    bypassMultiFilterLimit,
    agGridColumnConfig,
    showTotals,
    sumRowData,
    extraProps
  ) => {
    const { groupByColumns, detailTableServerSideDataSource, reportConfig, savedView } = extraProps;
    const sortColumn = reportConfig?.detailTableQuery.sortColumn;
    const hasGroupByColumns = groupByColumns?.length;
    const tableRowData = hasGroupByColumns ? undefined : rowData;
    const detailTableQuery = reportConfig?.detailTableQuery;
    const columnsToGroupBy = groupByColumns?.map((groupByColumnName) =>
      getColumnByNameFromList(groupByColumnName, tableColumns)
    );
    const groupByOptions = hasGroupByColumns
      ? {
          autoGroupColumnDef: {
            field: columnsToGroupBy[0].name,
            headerName: columnsToGroupBy[0].displayName,
            cellRendererSelector: null,
            pinned: 'left',
            minWidth: 256,
            width: 256, // this is the starting width which should match the css for the controls
            headerComponentParams: {
              isSorted: sortColumn === columnsToGroupBy[0].name
            }
          },
          cacheBlockSize: 250,
          rowModelType: 'serverSide',
          serverSideDatasource: detailTableServerSideDataSource,
          treeData: true,
          isServerSideGroup: (dataItem) => dataItem.isGroup,
          // strip off any child count at the end like " (3)"
          // or else you get no child output in the table
          // see: formatParentAndCount()
          getServerSideGroupKey: (dataItem) => dataItem[columnsToGroupBy[0].name].replace(childCountInParentName, '')
        }
      : {};

    // there are various things that can change that aggrid isn't going to notice
    // changes to this var causes aggrid to be rebuilt/restarted/reaped
    const reactKeyValue =
      agGridColumnConfig &&
      detailTableQuery &&
      JSON.stringify({
        agGridColumnConfig,
        filters: detailTableQuery.filter,
        sortColumn: detailTableQuery.sortColumn,
        filterBooleanOperators: detailTableQuery.filterBooleanOperators,
        sortDirection: detailTableQuery.sortDirection,
        groupBy: detailTableQuery.groupByColumns,
        savedViewId: savedView?.id
      });

    return {
      columnDefs,
      rowData: tableRowData,
      ...(showTotals && { pinnedBottomRowData: sumRowData }),
      gridOptions: {
        ...gridOptions,
        ...{ columnTypes },
        defaultColDef: { ...gridOptions.defaultColDef, suppressMovable: false },
        suppressDragLeaveHidesColumns: true,
        icons: {
          columnMoveHide: '<div/>'
        },
        ...groupByOptions,
        onRowGroupOpened: (params) => {
          const expanded = params.node.expanded;
          const eventKey = params.node.key;

          const keys = [];
          params.api.forEachNode((node) => {
            if (node.level === 0 && node.expanded) {
              keys.push(node.key);
            }
          });

          const existingKey = reportConfig.presentationConfiguration.groupByExpandedNodes.find(
            (configKey) => configKey === eventKey
          );

          if ((expanded && !existingKey) || (!expanded && existingKey)) {
            reportConfigurationActionCreators.updateGroupByExpandedNodes({ expandedNodes: keys });
            reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
          }
        }
      },
      filterProps,
      reactKeyValue,
      onColumnStateUpdate: (newColumnState) => {
        const config = newColumnState.map(createAgGridColumnConfig);
        reportConfigurationActionCreators.setAgGridColumnConfig(config);
        reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
      }
    } as IOgGridTableProps;
  }
);
